import { generateColorsMap } from "@mantine/colors-generator";
import {
    Alert,
    Badge,
    Button,
    createTheme,
    CSSVariablesResolver,
    DEFAULT_THEME,
    defaultVariantColorsResolver,
    Image,
    Loader,
    MantineColorsTuple,
    MantineProvider,
    mergeMantineTheme,
    rem,
    Text,
    Title,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { ReactNode } from "@tanstack/react-router";
import { useCustomer } from "shared/graphql/cust";
import { z } from "zod";

const mantineColorsTupleSchema = z
    .tuple([z.string(), z.string(), z.string(), z.string(), z.string(), z.string(), z.string(), z.string(), z.string(), z.string()])
    .transform((it) => it satisfies MantineColorsTuple);

function createThemeOverride(brandHex: string) {
    const brandColorMap = generateColorsMap(brandHex);

    return createTheme({
        primaryColor: "brand",
        autoContrast: true,
        primaryShade: {
            light: 8,
            dark: 7,
        },
        fontFamily: "Open Sans",
        colors: {
            brand: mantineColorsTupleSchema.parse(brandColorMap.colors.map((it) => it.hex())),
            "gray-accent": ["#000", "#000", "#000", "#000", "#000", "#000", "#000", "#232323", "#F1F3F5", "#000"],
        },
        spacing: {
            xs: rem(8),
        },
        headings: {
            sizes: {
                h1: {
                    fontSize: rem(20),
                    fontWeight: "600",
                },
                h2: {
                    fontSize: rem(20),
                    fontWeight: "400",
                },
                h3: {
                    fontSize: rem(16),
                    fontWeight: "600",
                },
            },
        },
        components: {
            Title: Title.extend({
                defaultProps: {
                    c: "text",
                },
            }),
            Image: Image.extend({
                defaultProps: {
                    flex: 0,
                },
            }),
            Text: Text.extend({
                defaultProps: {
                    c: "text",
                },
            }),
            Loader: Loader.extend({
                defaultProps: {
                    type: "dots",
                    mx: "auto",
                },
            }),
            Badge: Badge.extend({
                defaultProps: {
                    variant: "dot",
                    radius: "sm",
                },
            }),
            Button: Button.extend({
                defaultProps: {
                    w: "fit-content",
                },
            }),
            Alert: Alert.extend({
                styles: {
                    label: { color: "var(--mantine-color-text)" },
                    message: { color: "var(--mantine-color-text)" },
                },
            }),
            DateTimePicker: DateTimePicker.extend({
                defaultProps: {
                    clearButtonProps: {
                        "aria-label": "Clear",
                    },
                },
            }),
        },
        variantColorResolver: (input) => {
            const base = defaultVariantColorsResolver(input);

            // transparent button text color should be "text"
            if (input.variant == "transparent") {
                return { ...base, color: "text" };
            } else if (input.variant == "subtle") return { ...base, color: "text" };

            return base;
        },
    });
}

export function useTheme() {
    const { data: customer } = useCustomer();
    const override = createThemeOverride(customer.primaryColor);
    return mergeMantineTheme(DEFAULT_THEME, override);
}

/** Looks up the theme for the customer and provides it to the application via {@link MantineProvider} */
export const BryxEmnsMantineThemeProvider = (props: { children: ReactNode }) => {
    const theme = useTheme();
    return (
        <MantineProvider defaultColorScheme="auto" cssVariablesResolver={cssVariablesResolver} theme={theme}>
            {props.children}
        </MantineProvider>
    );
};

const cssVariablesResolver: CSSVariablesResolver = () => ({
    variables: {},
    // increase contrast for accessibility
    light: {
        "--mantine-color-dimmed": "#73737A",
        "--mantine-color-error": "var(--mantine-color-red-9)",
    },
    dark: {
        "--mantine-color-dimmed": "#C1C1C1",
        "--mantine-color-brand-text": "var(--mantine-color-brand-3)",
        "--mantine-color-anchor": "var(--mantine-color-brand-3)",
        "--mantine-color-error": "var(--mantine-color-red-5)",
    },
});
